import { Component, computed, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { IconField } from 'primeng/iconfield';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';

import { DatePipe } from '@angular/common';
import { DialogModule } from 'primeng/dialog';

import { environment } from '../../../environments/environment';
import { MODULE_ROUTE_PREFIX } from '../../core/modules/module-routes';
import { ServiceCatalogModule } from '../../core/umc/service-api.model';
import { CompanyContextService } from '../../services/company-context.service';
import { LicenseContextService, isWithheld } from '../../services/license-context.service';
import { ModuleRegistryService } from '../../services/module-registry.service';
import { ModuleCard } from './module-card';
import { ModuleCardModel, toModuleCard } from './module-card.model';

/**
 * The shell's landing screen: every module this installation offers.
 *
 * Reads the registry, which is fully resolved before the first render, so there
 * is no loading state to show — a tile is either openable or carries the reason
 * it is not. Modules disabled in `modules.config.json` never reach the registry
 * and so have no tile at all, which is the point of disabling them.
 */
@Component({
  selector: 'app-dashboard',
  imports: [
    ModuleCard,
    DatePipe,
    DialogModule,
    IconField,
    InputIcon,
    InputText,
    RouterLink,
    TranslatePipe,
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard {
  private readonly registry = inject(ModuleRegistryService);
  protected readonly licenses = inject(LicenseContextService);
  private readonly translate = inject(TranslateService);

  protected readonly search = signal('');

  /** Base segment of the module routes, for the service-page link. */
  protected readonly modulePrefix = MODULE_ROUTE_PREFIX;

  protected readonly companies = inject(CompanyContextService);

  /** The module whose licence dialog is open; null closes it. */
  protected readonly licenseInfoOf = signal<ModuleCardModel | null>(null);

  /** The catalogue entry behind the open dialog, when one arrived. */
  protected readonly licenseInfoCatalog = computed<ServiceCatalogModule | null>(() => {
    const card = this.licenseInfoOf();

    return card ? this.licenses.catalogEntryOf(card.id) : null;
  });

  protected openLicenseInfo(card: ModuleCardModel): void {
    this.licenses.loadLicenseDetails();
    this.licenseInfoOf.set(card);
  }

  /**
   * Registry tiles, license-checked: a module the active company holds no
   * licence for keeps its tile — hiding it would leave the user wondering
   * where the module went — but the tile stops linking, and the corner chip
   * says why. Licensed modules carry the green corner; modules outside the
   * catalog carry nothing.
   */
  protected readonly cards = computed(() =>
    this.registry.modules().map((module) => {
      const card = toModuleCard(module);

      if (!card.available) {
        return card;
      }

      // Nothing opens until the business card is complete — UMC refuses the
      // user's calls until then; the notice above the list says where to go.
      if (this.companies.cardIncomplete()) {
        return { ...card, available: false, noteKey: 'module.note.cardIncomplete' };
      }

      const state = this.licenses.licenseStateOf(card.id);

      if (isWithheld(state)) {
        return { ...card, available: false, license: state as 'noSeat' | 'unlicensed' };
      }

      return state === 'licensed' ? { ...card, license: state } : card;
    }),
  );

  /**
   * Where an incomplete business card is completed — the UMC console's card
   * screen. Null when the environment names no console: the notice then says
   * what to do without a link.
   */
  protected readonly cardUrl = cardUrlOf(environment.links?.umcConsoleUrl);

  protected readonly visibleCards = computed(() => {
    const term = this.search().trim().toLowerCase();

    if (!term) {
      return this.cards();
    }

    return this.cards().filter((card) =>
      this.searchableText(card).some((field) => field.toLowerCase().includes(term)),
    );
  });

  protected readonly hasModules = computed(() => this.cards().length > 0);

  /** As many skeleton rows as there will be tiles, so the page does not jump when they arrive. */
  protected readonly skeletonRows = computed(() =>
    Array.from({ length: Math.max(this.registry.modules().length, 1) }),
  );

  /**
   * Matches what the tile shows plus the id, so a user searches by the name
   * they read and an operator by the name they configured.
   *
   * `currentLang` is read to make this depend on the active language: the
   * translations come from `instant`, which is not reactive on its own.
   */
  private searchableText(card: ModuleCardModel): string[] {
    this.translate.currentLang();

    const text = [this.translate.instant(card.titleKey) as string, card.id];

    if (card.descriptionKey) {
      text.push(this.translate.instant(card.descriptionKey) as string);
    }

    if (card.category) {
      text.push(card.category);
    }

    return text;
  }
}

function cardUrlOf(consoleUrl: string | undefined): string | null {
  if (!consoleUrl) {
    return null;
  }

  const base = consoleUrl.replace(/\/+$/, '');

  return `${base}/customer/card`;
}
